import { useRef, useState, useCallback, useEffect } from 'react';
import _sortBy from 'lodash/sortBy';
import scrollIntoView from 'scroll-into-view-if-needed';

import { useDebounce } from '.';

/**
 * See IntersectionObserver docs: https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver
 * @typedef {IntersectionObserverOptions}
 * @property {HTMLElement | null} root
 * @property {string} rootMargin
 * @property {number} threshold
 */
interface IntersectionObserverOptions {
    root: HTMLElement | null;
    rootMargin: string;
    threshold: number;
}

/**
 * @typedef {UseIntersectionObserverInput}
 * @property {string} defaultSectionId the id to use as the initial activeSectionId
 * @property {string[]} sectionIds an array of DOM element ids to watch with IntersectionObserver
 * @property {IntersectionObserverOptions} observerOptions
 * @property {function} onSetActiveSectionId optional callback invoked when an id becomes the active
 * section id
 */
interface UseIntersectionObserverInput {
    defaultSectionId: string;
    sectionIds: string[];
    observerOptions: IntersectionObserverOptions;
    onSetActiveSectionId?(id: string): void;
    scrollIntoViewOptions?: ScrollIntoViewOptions;
}

/**
 * @typedef {UseSectionObserverResult}
 * @property {string} activeSectionId the currently active section id
 * @property {string[]} intersectionMap array of currently intersecting element ids, in order of
 * sectionIds from input
 * @property {function} observe a callback ref; i.e. a function that receives a DOM element when it
 * mounts and receives null when it unmounts. This must be passed as a ref to each DOM element that
 * needs to be observed. See: https://reactjs.org/docs/refs-and-the-dom.html#callback-refs
 * @property {function} scrollToSectionId a method for scrolling to a given DOM element by its id
 */
interface UseSectionObserverResult {
    activeSectionId: string;
    intersections: Intersection[];
    observe: Observe;
    scrollToSectionId: ScrollToSectionId;
}

const defaultScrollIntoViewOptions: ScrollIntoViewOptions = {
    behavior: 'smooth',
};

/**
 * Exposes an API for using IntersectionObserver to implement imperative scrolling. This makes it
 * possible to call a function which scrolls to the desired DOM element ID, as well as to trigger
 * a callback when a new DOM element ID becomes the currently "active" (i.e. visible) element.
 * @param {UseIntersectionObserverInput} input
 */
export const useIntersectionObserver = ({
    defaultSectionId,
    sectionIds,
    observerOptions,
    onSetActiveSectionId,
    scrollIntoViewOptions = defaultScrollIntoViewOptions,
}: UseIntersectionObserverInput): UseSectionObserverResult => {
    const [intersectionMap, setIntersectionMap] = useState<
        Map<string, Intersection>
    >(new Map<string, Intersection>());
    const [activeSectionId, setActiveSectionId] = useState(defaultSectionId);
    const setActiveSection = useCallback(
        (id: string): void => {
            setActiveSectionId(id);

            if (onSetActiveSectionId) {
                onSetActiveSectionId(id);
            }
        },
        [onSetActiveSectionId, setActiveSectionId],
    );

    const scrollToSectionId = useCallback(
        (sectionId: string): void => {
            const anchorTarget = document.getElementById(sectionId);

            if (anchorTarget !== null && setActiveSection) {
                setActiveSection(sectionId);

                const prefersReducedMotion = window.matchMedia(
                    '(prefers-reduced-motion: reduce)',
                ).matches;

                if (prefersReducedMotion) {
                    // If the user prefers reduced motion, scroll without animation
                    scrollIntoView(anchorTarget, {
                        behavior: actions => {
                            actions.forEach(action => {
                                const { top, left, el } = action;

                                el.scrollTop = top;
                                el.scrollLeft = left;
                            });
                        },
                    });
                } else {
                    // Otherwise, scroll with animation
                    scrollIntoView(anchorTarget, scrollIntoViewOptions);
                }
            }
        },
        [setActiveSection],
    );

    const observerCallback: IntersectionObserverCallback = useCallback(
        (entries): void => {
            const intersections: Intersection[] = [];

            entries.forEach(
                ({
                    isIntersecting,
                    target,
                }: IntersectionObserverEntry): void => {
                    const index =
                        sectionIds.findIndex(
                            (id: string): boolean => id === target.id,
                        ) ?? null;

                    if (index !== null) {
                        const intersection = {
                            id: target.id,
                            index,
                            isIntersecting,
                        };

                        intersections.push(intersection);
                    }
                },
            );

            const sortedIntersections = _sortBy(intersections, ['index']);

            const firstActiveIntersection = sortedIntersections.find(
                ({ isIntersecting }: Intersection): boolean => isIntersecting,
            );

            if (firstActiveIntersection) {
                setActiveSection(firstActiveIntersection.id);
            }

            setIntersectionMap(
                (
                    prev: Map<string, Intersection>,
                ): Map<string, Intersection> => {
                    const next = new Map<string, Intersection>(prev);

                    sortedIntersections.forEach(
                        (intersection: Intersection): void => {
                            next.set(intersection.id, intersection);
                        },
                    );

                    return next;
                },
            );
        },
        [setIntersectionMap, setActiveSection],
    );

    const observerRef = useRef<IntersectionObserver | null>(
        new IntersectionObserver(observerCallback, observerOptions),
    );

    useEffect(
        () => (): void => {
            if (observerRef?.current) {
                observerRef.current.disconnect();
            }
        },
        [],
    );

    const observe = useCallback(
        (target: HTMLElement | null): void => {
            if (observerRef?.current && target !== null) {
                observerRef.current.observe(target);
            }
        },
        [observerRef],
    );

    // Prevent the activeSectionId from bouncing around during a `scrollToSectionId()` call
    const debouncedActiveSectionId = useDebounce(activeSectionId, 300);

    return {
        activeSectionId: debouncedActiveSectionId,
        intersections: Array.from(intersectionMap.values()),
        observe,
        scrollToSectionId,
    };
};
